/** Team display data (colours, names) and NBA team ids for official assets. */
export type TeamInfo = {
  tricode: string;
  city: string;
  name: string;
  primary: string;
  secondary: string;
};

const T = (
  tricode: string,
  city: string,
  name: string,
  primary: string,
  secondary: string,
): TeamInfo => ({
  tricode,
  city,
  name,
  primary,
  secondary,
});

export const TEAMS: Record<string, TeamInfo> = Object.fromEntries(
  [
    T('ATL', 'Atlanta', 'Hawks', '#E03A3E', '#26282A'),
    T('BOS', 'Boston', 'Celtics', '#007A33', '#BA9653'),
    T('BKN', 'Brooklyn', 'Nets', '#000000', '#FFFFFF'),
    T('CHA', 'Charlotte', 'Hornets', '#1D1160', '#00788C'),
    T('CHI', 'Chicago', 'Bulls', '#CE1141', '#000000'),
    T('CLE', 'Cleveland', 'Cavaliers', '#860038', '#FDBB30'),
    T('DAL', 'Dallas', 'Mavericks', '#00538C', '#B8C4CA'),
    T('DEN', 'Denver', 'Nuggets', '#0E2240', '#FEC524'),
    T('DET', 'Detroit', 'Pistons', '#C8102E', '#1D42BA'),
    T('GSW', 'Golden State', 'Warriors', '#1D428A', '#FFC72C'),
    T('HOU', 'Houston', 'Rockets', '#CE1141', '#000000'),
    T('IND', 'Indiana', 'Pacers', '#002D62', '#FDBB30'),
    T('LAC', 'LA', 'Clippers', '#C8102E', '#1D428A'),
    T('LAL', 'Los Angeles', 'Lakers', '#552583', '#FDB927'),
    T('MEM', 'Memphis', 'Grizzlies', '#5D76A9', '#12173F'),
    T('MIA', 'Miami', 'Heat', '#98002E', '#F9A01B'),
    T('MIL', 'Milwaukee', 'Bucks', '#00471B', '#EEE1C6'),
    T('MIN', 'Minnesota', 'Timberwolves', '#0C2340', '#78BE20'),
    T('NOP', 'New Orleans', 'Pelicans', '#0C2340', '#C8102E'),
    T('NYK', 'New York', 'Knicks', '#006BB6', '#F58426'),
    T('OKC', 'Oklahoma City', 'Thunder', '#007AC1', '#EF3B24'),
    T('ORL', 'Orlando', 'Magic', '#0077C0', '#C4CED4'),
    T('PHI', 'Philadelphia', '76ers', '#006BB6', '#ED174C'),
    T('PHX', 'Phoenix', 'Suns', '#1D1160', '#E56020'),
    T('POR', 'Portland', 'Trail Blazers', '#E03A3E', '#000000'),
    T('SAC', 'Sacramento', 'Kings', '#5A2D81', '#63727A'),
    T('SAS', 'San Antonio', 'Spurs', '#000000', '#C4CED4'),
    T('TOR', 'Toronto', 'Raptors', '#CE1141', '#000000'),
    T('UTA', 'Utah', 'Jazz', '#002B5C', '#F9A01B'),
    T('WAS', 'Washington', 'Wizards', '#002B5C', '#E31837'),
  ].map((t) => [t.tricode, t]),
);

export function team(tricode: string): TeamInfo {
  return (
    TEAMS[tricode] ?? {
      tricode,
      city: tricode,
      name: tricode,
      primary: '#444444',
      secondary: '#999999',
    }
  );
}

/** NBA team ids by tricode (derived from the 2025-26 archive). ESPN-source games carry ESPN ids, so look up by tricode. */
export const NBA_TEAM_IDS: Readonly<Record<string, number>> = {
  ATL: 1610612737,
  BKN: 1610612751,
  BOS: 1610612738,
  CHA: 1610612766,
  CHI: 1610612741,
  CLE: 1610612739,
  DAL: 1610612742,
  DEN: 1610612743,
  DET: 1610612765,
  GSW: 1610612744,
  HOU: 1610612745,
  IND: 1610612754,
  LAC: 1610612746,
  LAL: 1610612747,
  MEM: 1610612763,
  MIA: 1610612748,
  MIL: 1610612749,
  MIN: 1610612750,
  NOP: 1610612740,
  NYK: 1610612752,
  OKC: 1610612760,
  ORL: 1610612753,
  PHI: 1610612755,
  PHX: 1610612756,
  POR: 1610612757,
  SAC: 1610612758,
  SAS: 1610612759,
  TOR: 1610612761,
  UTA: 1610612762,
  WAS: 1610612764,
};

export const nbaTeamId = (tricode: string): number | undefined => NBA_TEAM_IDS[tricode];
